import { FC, useMemo } from "react";
import { useLingui } from "@lingui/react";
import { weekdays } from "../Weekdays.ts";
import { TimeSlot } from "../../api/scheduleApi.ts";
import ActionButton from "../ActionButton.tsx";
import Container from "../Container.tsx";
import ContainerHeader from "../ContainerHeader.tsx";
import { Trans } from "@lingui/react/macro";
import { useLanguageStore } from "../../stores/languageStore.ts";
import { format, setHours, setMinutes, startOfToday } from "date-fns";
import { LocationWithName } from "../../api/locationApi.ts";

interface TimeslotListProps {
  timeslots: TimeSlot[];
  onDeleteTimeslot: (updateLocationId: string, id: string) => void;
  deletingId: string | null;
  locations: LocationWithName[];
  hideLocation?: boolean;
}

const TimeslotList: FC<TimeslotListProps> = ({
  timeslots,
  onDeleteTimeslot,
  deletingId,
  locations,
  hideLocation,
}) => {
  const { i18n } = useLingui();
  const { dateFnsLocale } = useLanguageStore();
  const handleDeleteClick = (timeSlot: TimeSlot) => {
    onDeleteTimeslot(timeSlot.locationId, timeSlot.id);
  };

  const getFormattedTime = (hour: string, minutes: string) => {
    const baseDate = startOfToday();
    const hourAndMinutesDate = setMinutes(
      setHours(baseDate, Number(hour)),
      Number(minutes),
    );
    return format(hourAndMinutesDate, "p", {
      locale: dateFnsLocale,
    });
  };

  // Group timeslots by weekday and sort by weekday order based on locale
  const groupedTimeslots = useMemo(() => {
    // Get the week start day from the locale (0 = Sunday, 1 = Monday, etc.)
    const weekStartsOn = dateFnsLocale?.options?.weekStartsOn ?? 1; // Default to Monday if not specified

    // Since weekdays array starts with Monday (index 0), we need to adjust:
    // weekStartsOn: 0 (Sunday) -> reorder to put Sunday first
    // weekStartsOn: 1 (Monday) -> keep original order (Monday first)
    let reorderedWeekdays;
    if (weekStartsOn === 0) {
      // Week starts on Sunday, so move Sunday (last item) to front
      reorderedWeekdays = [weekdays[6], ...weekdays.slice(0, 6)]; // Sunday + Mon-Sat
    } else {
      // Week starts on Monday or other day, keep original order
      reorderedWeekdays = weekdays;
    }

    return reorderedWeekdays.reduce(
      (acc, weekday) => {
        const dayTimeslots = timeslots.filter(
          (timeslot) => timeslot.weekday === weekday.value,
        );
        if (dayTimeslots.length > 0) {
          acc.push({
            weekday: weekday,
            timeslots: dayTimeslots.sort((a, b) => {
              // Sort by start time within each day
              const aStart =
                parseInt(a.startHour) * 60 + parseInt(a.startMinute);
              const bStart =
                parseInt(b.startHour) * 60 + parseInt(b.startMinute);
              return aStart - bStart;
            }),
          });
        }
        return acc;
      },
      [] as Array<{ weekday: (typeof weekdays)[0]; timeslots: TimeSlot[] }>,
    );
  }, [timeslots, dateFnsLocale]);

  return (
    <Container>
      <ContainerHeader>
        <Trans>Your Timeslots</Trans>
      </ContainerHeader>
      {groupedTimeslots.length === 0 ? (
        <p className="text-gray-500">
          <Trans>No timeslots added yet.</Trans>
        </p>
      ) : (
        <div className="space-y-6">
          {groupedTimeslots.map(({ weekday, timeslots: dayTimeslots }) => (
            <div key={weekday.value}>
              <h3 className="mb-3 text-lg font-semibold text-gray-900">
                {i18n._(weekday.label)}
              </h3>
              <ul className="divide-y divide-gray-200 rounded-lg border border-gray-200">
                {dayTimeslots.map((timeslot) => {
                  const isDeleting = deletingId === timeslot.id;
                  const anyDeleting = deletingId !== null;

                  const startTimeFormatted = getFormattedTime(
                    timeslot.startHour,
                    timeslot.startMinute,
                  );
                  const endTimeFormatted = getFormattedTime(
                    timeslot.endHour,
                    timeslot.endMinute,
                  );
                  const location = locations.find(
                    ({ id }) => id === timeslot.locationId,
                  );

                  return (
                    <li
                      key={timeslot.id}
                      className="flex items-center justify-between px-4 py-3"
                    >
                      <div>
                        <p className="text-sm text-gray-900">
                          {startTimeFormatted} - {endTimeFormatted}
                          {!hideLocation && ` • ${location?.name}`}
                        </p>
                      </div>
                      <ActionButton
                        onClick={() => handleDeleteClick(timeslot)}
                        disabled={anyDeleting}
                        isLoading={isDeleting}
                        className="ml-4 rounded-full px-3 py-1.5 text-xs"
                      >
                        <Trans>Delete</Trans>
                      </ActionButton>
                    </li>
                  );
                })}
              </ul>
            </div>
          ))}
        </div>
      )}
    </Container>
  );
};

export default TimeslotList;
